import { SKILL_BY_ID } from "@/modules/lernpfad/data/skills"
import type { Baustein, SkillCategory } from "@/modules/lernpfad/engine"
import type { UiKey } from "@/modules/lernpfad/i18n/ui"

/**
 * Deterministic quiz generation, per Baustein, from what it teaches.
 *
 * Three questions, one per `teaches` skill (cycling if there are fewer than
 * three), each built from a category-level template rather than authored per
 * course — 61 Bausteine × up to 3 skills would mean hand-writing ~180
 * questions; instead there are 3 template "slots" per `SkillCategory` (5
 * categories), each with one stem and four options, and the skill's own label
 * is substituted into the stem. This is also more honest about what an
 * auto-generated LMS quiz actually looks like: real ones are templated too.
 *
 * The four options per slot are FIXED content (one correct + three
 * distractors), translated once via `ui.ts` like everything else user-facing.
 * What is generated per Baustein is only the DISPLAY ORDER of those four —
 * seeded from `bausteinId:slot` so a re-take shows the same order, but the
 * correct answer is not predictably in the same position across questions.
 */

export type QuizOptionKind = "correct" | "d1" | "d2" | "d3"

export interface QuizQuestion {
    bausteinId: string
    /** Position in the quiz, 0-based — also the template "slot" (purpose /
     *  behaviour / edge case), so `slot` doubles as an index into the category's
     *  three templates. */
    slot: 0 | 1 | 2
    skillId: string
    category: SkillCategory
    stemKey: UiKey
    /** Four options in DISPLAY order; `correctIndex` names which one is right. */
    optionKeys: [UiKey, UiKey, UiKey, UiKey]
    correctIndex: number
}

/** Stable 32-bit FNV-1a — the same hash used everywhere else in this module. */
function fnv1a(input: string): number {
    let hash = 0x811c9dc5
    for (let index = 0; index < input.length; index++) {
        hash ^= input.charCodeAt(index)
        hash = Math.imul(hash, 0x01000193)
    }
    return hash >>> 0
}

/** Deterministic Fisher–Yates, seeded by a string rather than by `Math.random`. */
function seededShuffle<T>(items: T[], seed: string): T[] {
    const shuffled = [...items]
    let state = fnv1a(seed)
    for (let i = shuffled.length - 1; i > 0; i--) {
        state = fnv1a(String(state))
        const j = state % (i + 1)
        ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
    }
    return shuffled
}

/**
 * Three questions for one Baustein.
 *
 * Empty only for a Baustein that teaches nothing — none exist in the
 * catalogue today, but a future data-only addition should not crash the
 * player rather than silently produce a 0-question quiz.
 */
export function generateQuiz(baustein: Baustein): QuizQuestion[] {
    const taught = baustein.teaches
    if (taught.length === 0) return []

    const questions: QuizQuestion[] = []
    for (let slot = 0; slot < 3; slot++) {
        const entry = taught[slot % taught.length]
        const category = SKILL_BY_ID.get(entry.skillId)?.category
        // Every skill in the catalogue has a category; this guards a future
        // data gap rather than a case that occurs today.
        if (!category) continue

        const kinds: QuizOptionKind[] = seededShuffle(
            ["correct", "d1", "d2", "d3"],
            `${baustein.id}:${slot}:options`,
        )
        const optionKeys = kinds.map(
            (kind) => `lms.quiz.${category}.${slot}.${kind}` as UiKey,
        ) as [UiKey, UiKey, UiKey, UiKey]

        questions.push({
            bausteinId: baustein.id,
            slot: slot as 0 | 1 | 2,
            skillId: entry.skillId,
            category,
            stemKey: `lms.quiz.${category}.${slot}.stem` as UiKey,
            optionKeys,
            correctIndex: kinds.indexOf("correct"),
        })
    }
    return questions
}
